import { useState } from "react";
import { CalendarClock, CheckCircle2, SignatureIcon, X } from "lucide-react";
import { toast } from "sonner";

import { Folder, SigningStatus } from "../../redux/interfaces/folder.types.ts";
import { updateFolder } from "../../redux/actions/folderActions.tsx";
import { useAppDispatch } from "../../redux/hooks.ts";
import { Button } from "../ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog.tsx";
import { Label } from "../ui/label.tsx";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group.tsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip.tsx";
import {
  DateField,
  TimeField,
  formatDateTimeForApi,
} from "./DateTimePicker.tsx";

interface Props {
  folder: Folder;
  signingStatus: SigningStatus;
  setSigningStatus: (status: SigningStatus) => void;
  signingDate: string | null;
  setSigningDate: (date: string) => void;
}

type AllSigners = "yes" | "no";

export const SignContract: React.FC<Props> = ({
  folder,
  setSigningStatus,
  signingDate,
  setSigningDate,
}) => {
  const dispatch = useAppDispatch();
  const hasContractGenerated = folder.is_lease_contract_generated;

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"sign" | "cancel">("sign");
  const [allSigners, setAllSigners] = useState<AllSigners | undefined>(undefined);
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [hour, setHour] = useState<string>("");
  const [minute, setMinute] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const handleOpen = () => {
    if (!hasContractGenerated) return;
    setTab("sign");
    setAllSigners(undefined);
    const parsed = signingDate ? new Date(signingDate) : null;
    if (parsed && !isNaN(parsed.getTime())) {
      setDate(parsed);
      setHour(String(parsed.getHours()).padStart(2, "0"));
      setMinute(String(parsed.getMinutes()).padStart(2, "0"));
    } else {
      setDate(undefined);
      setHour("");
      setMinute("");
    }
    setOpen(true);
  };

  const submit = async () => {
    if (!date || !hour || !minute) return;
    if (tab === "sign" && !allSigners) return;

    const status: SigningStatus =
      tab === "cancel" ? "cancelled" : allSigners === "yes" ? "signed" : "partial";
    const combined = formatDateTimeForApi(date, hour, minute);

    setLoading(true);
    try {
      await dispatch(
        updateFolder({
          folderId: folder.id,
          data: {
            lease_contract_signing_status: status,
            lease_contract_sign_date: combined,
          },
        }),
      ).unwrap();

      setSigningStatus(status);
      setSigningDate(combined);
      toast.success(
        tab === "cancel"
          ? "Cancelación confirmada"
          : status === "partial"
            ? "Firma parcial registrada"
            : "Firma confirmada",
      );
      setOpen(false);
    } catch {
      toast.error("Error al guardar el estado");
    } finally {
      setLoading(false);
    }
  };

  const canSubmit =
    !!date &&
    !!hour &&
    !!minute &&
    !loading &&
    (tab === "cancel" || !!allSigners);

  const handleTimeChange = (h: string, m: string) => {
    setHour(h);
    setMinute(m);
  };

  const handleNow = () => {
    const now = new Date();
    setDate(now);
    setHour(String(now.getHours()).padStart(2, "0"));
    setMinute(String(now.getMinutes()).padStart(2, "0"));
  };

  const renderDateTimeFields = (dateLabel: string) => (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-[1fr_auto] gap-3">
        <div className="flex flex-col gap-2">
          <Label>{dateLabel}</Label>
          <DateField value={date} onChange={setDate} />
        </div>
        <div className="flex flex-col gap-2">
          <Label>Hora</Label>
          <TimeField hour={hour} minute={minute} onChange={handleTimeChange} />
        </div>
      </div>
      <Button
        type="button"
        variant="link"
        size="sm"
        onClick={handleNow}
        className="h-auto self-start p-0 text-xs"
      >
        <CalendarClock className="h-3.5 w-3.5" />
        Usar fecha y hora actual
      </Button>
    </div>
  );

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={handleOpen}
            disabled={!hasContractGenerated}
            className={
              hasContractGenerated
                ? "text-gray-600 hover:text-blue-500"
                : "text-gray-300 cursor-not-allowed"
            }
          >
            <SignatureIcon size={18} />
          </button>
        </TooltipTrigger>
        <TooltipContent>Estado de firma de contrato</TooltipContent>
      </Tooltip>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Estado de Firma</DialogTitle>
            <DialogDescription>
              Contrato de Arrendamiento #{folder.folder_number}
            </DialogDescription>
          </DialogHeader>

          <Tabs value={tab} onValueChange={(v) => setTab(v as "sign" | "cancel")}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="sign">Firmar</TabsTrigger>
              <TabsTrigger value="cancel">Cancelar</TabsTrigger>
            </TabsList>

            <TabsContent value="sign" className="mt-4 flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label>¿Firmaron todas las partes?</Label>
                <RadioGroup
                  value={allSigners ?? ""}
                  onValueChange={(v) => setAllSigners(v as AllSigners)}
                  className="flex gap-6"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="yes" id="all-signers-yes" />
                    <Label
                      htmlFor="all-signers-yes"
                      className="cursor-pointer font-normal"
                    >
                      Sí
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="no" id="all-signers-no" />
                    <Label
                      htmlFor="all-signers-no"
                      className="cursor-pointer font-normal"
                    >
                      No (firma parcial)
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {renderDateTimeFields("Fecha de Firma")}
            </TabsContent>

            <TabsContent value="cancel" className="mt-4 flex flex-col gap-4">
              {renderDateTimeFields("Fecha de Cancelación")}
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cerrar
            </Button>
            {tab === "sign" ? (
              <Button
                onClick={submit}
                disabled={!canSubmit}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle2 className="mr-2 h-4 w-4" />
                {allSigners === "no"
                  ? "Confirmar Firma Parcial"
                  : "Confirmar Firma"}
              </Button>
            ) : (
              <Button
                variant="destructive"
                onClick={submit}
                disabled={!canSubmit}
              >
                <X className="mr-2 h-4 w-4" />
                Confirmar Cancelación
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};